import { useEffect, useMemo, useRef, useState } from "react";
import { BillingDetails } from "../../../../../../api";
import { useGlobalNotifications } from "../../../../../../hooks/use-global-notifications";

export type EditDetailsModalProps = {
  open: boolean;
  initial: BillingDetails | null;
  onClose: () => void;
  onSave: (details: BillingDetails) => void;
  onNavigateToCheckout?: () => void;
  checkoutFlow?: boolean;
};

type UseEditDetailsModalParams = EditDetailsModalProps;

export function useEditDetailsModal({
  open,
  initial,
  onClose,
  onSave,
  onNavigateToCheckout,
  checkoutFlow = false,
}: UseEditDetailsModalParams) {
  const { showError, showSuccess } = useGlobalNotifications();

  const defaultDetails: BillingDetails = {
    firstName: "",
    lastName: "",
    companyName: "",
    streetAddress: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    vatTaxId: "",
    billingEmail: "",
  };

  const [details, setDetails] = useState<BillingDetails>(
    initial || defaultDetails,
  );
  const [errors, setErrors] = useState<
    Partial<Record<keyof BillingDetails | "general", string>>
  >(
    {},
  );
  const [saving, setSaving] = useState(false);
  const [originalDetails, setOriginalDetails] = useState<BillingDetails | null>(
    null,
  );
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Reset when opened with fresh initial
  useEffect(() => {
    if (!open) return;
    const detailsToUse = initial || defaultDetails;
    setDetails(detailsToUse);
    setOriginalDetails(detailsToUse);
    setErrors({});
    setShowUnsavedModal(false);
  }, [open, initial]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCloseAttempt();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const hasChanges = () => {
    if (!originalDetails) return false;
    return (
      details.firstName !== originalDetails.firstName ||
      details.lastName !== originalDetails.lastName ||
      details.companyName !== originalDetails.companyName ||
      details.streetAddress !== originalDetails.streetAddress ||
      details.city !== originalDetails.city ||
      details.stateProvince !== originalDetails.stateProvince ||
      details.postalCode !== originalDetails.postalCode ||
      details.country !== originalDetails.country ||
      details.vatTaxId !== originalDetails.vatTaxId ||
      details.billingEmail !== originalDetails.billingEmail
    );
  };

  const handleCloseAttempt = () => {
    if (hasChanges()) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    onClose();
  };

  const handleSaveChanges = async () => {
    setShowUnsavedModal(false);
    await handleSave();
  };

  const handleSave = async () => {
    const newErrors: Partial<Record<keyof BillingDetails | "general", string>> =
      {};
    if (!details.firstName.trim()) {
      newErrors.firstName = "Please enter your first name.";
    }
    if (!details.lastName.trim()) {
      newErrors.lastName = "Please enter your last name.";
    }
    if (!details.companyName.trim()) {
      newErrors.companyName = "Please enter your company name.";
    }
    if (!details.streetAddress.trim()) {
      newErrors.streetAddress = "Please enter your street address.";
    }
    if (!details.city.trim()) newErrors.city = "Please enter your city.";
    if (!details.stateProvince.trim()) {
      newErrors.stateProvince = "Please enter your state/province.";
    }
    if (!details.postalCode.trim()) {
      newErrors.postalCode = "Please enter your postal code.";
    }
    if (!details.country.trim()) {
      newErrors.country = "Please select your country.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    try {
      setSaving(true);
      await onSave(details);
      setSaving(false);
      showSuccess("Billing details saved successfully");
      // Navigate to checkout if in checkout flow
      if (checkoutFlow && onNavigateToCheckout) {
        onNavigateToCheckout();
      }
    } catch (error: any) {
      setSaving(false);
      console.error("Failed to save billing details:", error);
      const errorMsg = error?.response?.data?.detail ||
        "Failed to save billing details. Please try again.";
      setErrors({
        general: errorMsg,
      });
      showError(errorMsg);
    }
  };

  const disabled = useMemo(() => {
    return saving;
  }, [saving]);

  return {
    panelRef,
    details,
    setDetails,
    errors,
    setErrors,
    saving,
    showUnsavedModal,
    setShowUnsavedModal,
    hasChanges,
    handleCloseAttempt,
    handleDiscardChanges,
    handleSaveChanges,
    handleSave,
    disabled,
  };
}
